/**
 * Chassis-page Backups tab — running-config snapshot history +
 * "Take backup now" trigger.
 *
 * Reuses ``GET /api/chassis/{id}/backups`` (v1's existing endpoint).
 * Submitting a backup_running_config job hands it off to the inline
 * job tracker (sticky bottom of the Inventory tab) via the custom
 * ``inline-job-tracker.add`` DOM event.
 */
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Download, Eye, RefreshCw } from "lucide-react";
import { useState } from "react";

import { api } from "../../../../api/client";
import { LocalTime } from "../../../../lib/datetime";
import { CopyableCell } from "../../../components/CopyableCell";
import { useToast } from "../../../components/Toast";

type BackupSummary = {
  id: number;
  chassis_id: number;
  taken_at: string;
  taken_by_gigafy_username: string;
  source: string;
  byte_len: number;
  sha256: string;
  parsed_profiles_at: string | null;
};

type BackupDetail = BackupSummary & { content: string };

export function BackupsTab({ chassisId }: { chassisId: string }) {
  const qc = useQueryClient();
  const toast = useToast();
  const [openId, setOpenId] = useState<number | null>(null);

  const list = useQuery<{ backups: BackupSummary[] }>({
    queryKey: ["v2", "chassis", chassisId, "backups"],
    queryFn: async () => {
      const res = await api.get<{ backups: BackupSummary[] }>(
        `/chassis/${chassisId}/backups`,
      );
      return res.data;
    },
    refetchInterval: 30_000,
  });

  const detail = useQuery<BackupDetail>({
    queryKey: ["v2", "chassis", chassisId, "backup", openId],
    queryFn: async () => {
      const res = await api.get<BackupDetail>(
        `/chassis/${chassisId}/backups/${openId}`,
      );
      return res.data;
    },
    enabled: openId !== null,
  });

  const takeBackup = useMutation({
    mutationFn: async () => {
      const res = await api.post<{ id: string }>(
        `/chassis/${chassisId}/jobs`,
        { name: "backup_running_config", params: {} },
      );
      return res.data;
    },
    onSuccess: ({ id }) => {
      // Hand off to InlineJobTracker via the custom DOM event
      // (compatible with v1's pattern — see VdslPortsCard:99).
      window.dispatchEvent(
        new CustomEvent("inline-job-tracker.add", {
          detail: { jobId: id, kind: "backup_running_config" },
        }),
      );
      toast.push("Backup job queued", "success");
      qc.invalidateQueries({ queryKey: ["v2", "chassis", chassisId, "backups"] });
    },
  });

  const backups = list.data?.backups ?? [];

  return (
    <section className="flex flex-col gap-3 rounded-lg bg-slate-900 shadow-border p-4">
      <div className="flex flex-wrap items-baseline justify-between gap-2">
        <h2 className="text-sm font-semibold uppercase tracking-wide text-slate-400">
          Running-config backups
        </h2>
        <button
          type="button"
          onClick={() => takeBackup.mutate()}
          disabled={takeBackup.isPending}
          className="inline-flex items-center gap-1 rounded border border-slate-700 bg-slate-800 px-2 py-1 text-xs text-slate-200 hover:bg-slate-700 disabled:opacity-50"
          title="Run show / display running-config and store the result"
        >
          <RefreshCw className="h-3 w-3" />
          {takeBackup.isPending ? "Queueing…" : "Take backup now"}
        </button>
      </div>

      {list.isLoading ? (
        <p className="text-xs text-slate-500">Loading…</p>
      ) : backups.length === 0 ? (
        <p className="text-xs text-slate-500">
          No backups yet — click <em>Take backup now</em> above.
        </p>
      ) : (
        <table className="w-full text-xs">
          <thead className="text-left text-[10px] uppercase tracking-wide text-slate-500">
            <tr>
              <th className="px-2 py-1">Taken</th>
              <th className="px-2 py-1">By</th>
              <th className="px-2 py-1">Source</th>
              <th className="px-2 py-1 text-right">Bytes</th>
              <th className="px-2 py-1">SHA256</th>
              <th className="px-2 py-1 text-right">Actions</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-800/60 text-slate-300">
            {backups.map((b) => (
              <tr key={b.id}>
                <td className="px-2 py-1.5 font-mono text-[10px] text-slate-500">
                  <LocalTime iso={b.taken_at} />
                </td>
                <td className="px-2 py-1.5">{b.taken_by_gigafy_username}</td>
                <td className="px-2 py-1.5">
                  <span className="rounded border border-slate-700 px-1.5 py-0.5 text-[10px] uppercase tracking-wide text-slate-400">
                    {b.source}
                  </span>
                </td>
                <td className="px-2 py-1.5 text-right font-mono">
                  {b.byte_len.toLocaleString()}
                </td>
                <td className="px-2 py-1.5">
                  <CopyableCell value={b.sha256} display={b.sha256.slice(0, 12) + "…"} />
                </td>
                <td className="px-2 py-1.5 text-right">
                  <div className="inline-flex items-center gap-2">
                    <button
                      type="button"
                      onClick={() => setOpenId(b.id)}
                      className="inline-flex items-center gap-1 text-emerald-400 hover:text-emerald-300"
                    >
                      <Eye className="h-3 w-3" />
                      View
                    </button>
                    <a
                      href={`/api/chassis/${chassisId}/backups/${b.id}/raw`}
                      className="inline-flex items-center gap-1 text-slate-300 hover:text-slate-100"
                    >
                      <Download className="h-3 w-3" />
                      Download
                    </a>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}

      {openId !== null && (
        // In-page slide-over (not a separate route) so the operator
        // never leaves the chassis page. Closes via the X or Esc.
        <div
          className="fixed inset-0 z-40 flex items-center justify-center bg-slate-950/80 p-4"
          onClick={() => setOpenId(null)}
        >
          <div
            className="flex max-h-[90vh] w-full max-w-5xl flex-col overflow-hidden rounded-lg bg-slate-900 shadow-border shadow-2xl"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-baseline justify-between border-b border-slate-800 px-4 py-2">
              <h3 className="text-sm font-medium text-slate-100">
                Backup #{openId} ·{" "}
                <span className="font-mono text-xs text-slate-500">
                  <LocalTime iso={detail.data?.taken_at} />
                </span>
              </h3>
              <button
                type="button"
                onClick={() => setOpenId(null)}
                className="rounded border border-slate-700 px-2 py-1 text-xs text-slate-300 hover:bg-slate-800"
              >
                Close
              </button>
            </div>
            <pre className="flex-1 overflow-auto bg-slate-950 p-3 font-mono text-[11px] text-slate-300">
              {detail.isLoading ? "Loading…" : detail.data?.content ?? ""}
            </pre>
          </div>
        </div>
      )}
    </section>
  );
}
